import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import type { Insole } from "~/api/insoles";
import { deleteInsole } from "~/api/insoles";
import { errorToast, useToastStore } from "~/components";
import { Button, Modal } from "~/ui";

interface DeleteInsoleConfirmationModalProps {
  isOpen: boolean;
  insole: Insole;
  onClose: () => void;
}

export function DeleteInsoleConfirmationModal({
  isOpen,
  insole,
  onClose,
}: DeleteInsoleConfirmationModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { pushToast } = useToastStore();

  const { mutate: deleteInsoleMutation } = useMutation({
    mutationFn: deleteInsole,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["getInsoles"],
      });
      onClose();
      void pushToast({
        type: "success",
        message: t("insoles.delete_success"),
      });
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  const onSubmit = () => {
    deleteInsoleMutation(insole.id);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t("insoles.delete_insole")}>
      <p className="mb-8 text-left text-lg text-neutral-50 dark:text-neutral-80">
        {t("insoles.delete_confirmation", {
          serialNumber: insole.serial_number,
        })}
      </p>

      <div className="flex w-full gap-3">
        <Button
          variant="outline"
          onClick={onClose}
          text={t("general.cancel")}
          className="flex grow"
        />

        <Button
          variant="filled"
          onClick={onSubmit}
          text={t("general.remove")}
          className="flex grow"
        />
      </div>
    </Modal>
  );
}
